import { ModalOverlay } from '@dev-spendesk/grapes';
import React, { useState } from 'react';

import {
  type AuthenticationFactor,
  type AuthenticationMethod,
} from 'src/auth/modules/StrongCustomerAuthentication/models/factors';
import { ModalMFAToken } from 'src/core/common/components/ModalMFA/ModalMFAToken/ModalMFAToken';

import { AuthenticationMethodListModal } from './AuthenticationMethodListModal';
import { ContactAdminModal } from './ContactAdminModal';
import { RecoveryModal } from './RecoveryModal';
import { TotpModal } from './TotpModal';

type FlowProps =
  | { flow: 'auth'; scaToken: string; operationId?: never }
  | { flow: 'standard'; scaToken?: never; operationId?: string };

type Props = {
  factors: Array<AuthenticationFactor>;
  isOpen: boolean;
  onAlternateAuthenticationMethodSelected: (
    selectedFactor: AuthenticationFactor,
  ) => Promise<void>;
  onClose: () => Promise<void>;
  onScaVerified: () => Promise<void>;
  otpChannel?: 'email' | 'phone';
} & FlowProps;

type Step = 'selectMethod' | 'verifyOperation' | 'recoveryFlow';

export const AlternateAuthenticationMethodModal = ({
  factors,
  flow,
  isOpen,
  onAlternateAuthenticationMethodSelected,
  onClose,
  onScaVerified,
  operationId,
  otpChannel,
  scaToken,
}: Props) => {
  const [currentStep, setStep] = useState<Step>('selectMethod');
  const [selectedFactor, setSelectedFactor] = useState<
    AuthenticationFactor | undefined
  >(undefined);

  const recoveryFactor = factors.find((f) => f.type === 'RECOVERY');

  const goBackToMethodSelection = () => {
    setStep('selectMethod');
  };

  const handleNext = async (authenticationMethod: AuthenticationMethod) => {
    const factor = factors.find((f) => f.type === authenticationMethod);
    if (factor) {
      setSelectedFactor(factor);
      await onAlternateAuthenticationMethodSelected(factor);
      setStep('verifyOperation');
    }
  };

  const handleLostAccess = () => {
    setStep('recoveryFlow');
  };

  return (
    <>
      <ModalOverlay isOpen={isOpen}>
        {currentStep === 'selectMethod' && (
          <AuthenticationMethodListModal
            authenticationMethod={selectedFactor?.type}
            factors={factors}
            onClose={onClose}
            onLostAccess={handleLostAccess}
            onNext={handleNext}
            showLostAccessLink={flow === 'auth'}
          />
        )}
        {currentStep === 'verifyOperation' &&
          selectedFactor &&
          selectedFactor.type === 'TOTP' && (
            <TotpModal
              {...(flow === 'auth'
                ? { flow, scaToken }
                : { flow, operationId })}
              onBack={goBackToMethodSelection}
              onTotpTokenValidated={onScaVerified}
            />
          )}
        {currentStep === 'verifyOperation' &&
          selectedFactor &&
          selectedFactor.type === 'RECOVERY' && (
            <RecoveryModal
              {...(flow === 'auth'
                ? { flow, scaToken }
                : { flow, operationId })}
              onBack={goBackToMethodSelection}
              onRecoveryTokenValidated={onScaVerified}
            />
          )}
        {currentStep === 'recoveryFlow' && (
          <ContactAdminModal
            hasRecoveryMethod={!!recoveryFactor}
            onCancel={goBackToMethodSelection}
            onNext={handleNext}
          />
        )}
      </ModalOverlay>
      {currentStep === 'verifyOperation' &&
        selectedFactor &&
        selectedFactor.type === 'SMS' && (
          <ModalMFAToken
            {...(flow === 'auth' ? { flow, scaToken } : { flow })}
            isOpen
            onMfaTokenValidated={onScaVerified}
            onClose={goBackToMethodSelection}
            otpChannel={otpChannel}
          />
        )}
    </>
  );
};
